import React, { useEffect, useState } from 'react';
import { Button } from 'reactstrap';

// Thumbs up / down for one note generation (Comprehend, refresh, etc.): the "Note complete" bar, plus a
// "What was off?" panel after a thumbs down. UI only - the parent decides what
// to do with each answer.
//
// onSubmit receives the latest full answer for this click, so the last call wins:
//   { rating: 'up' }                                on thumbs up
//   { rating: 'down' }                              on thumbs down
//   { rating: 'down', reasons, sections, comment }  on Send feedback
// Skip and the close button only hide the panel; the thumbs down already counted.
// Once rated (and the panel is closed), "Thanks!" shows briefly, then onDone.
//
// sectionChoices are the section keys the rated run regenerated, or null for the
// whole note (the standard list). One section is already known, so the question
// is not asked; several are offered as the only choices.

const GREEN_TEXT = '#0f7a57';
const GREEN_BORDER = '#c3e8d6';
const SLATE = '#4a5578';
const GREEN = '#1d9e75';
const TEXT = '#3d4a6b';
const MUTED = '#6b7280';
const CHIP_BORDER = '#d6dbe3';

// `id` is what gets stored, so the wording can change without breaking the dashboard.
const REASONS = [
  { id: 'missing_info', label: 'Missing info' },
  { id: 'info_not_said', label: 'Info I didn’t say' },
  { id: 'details_wrong', label: 'Got details wrong (side, numbers, gender)' },
  { id: 'too_long', label: 'Too long / wordy' },
  { id: 'repetitive', label: 'Repetitive info' },
  { id: 'wrong_section', label: 'Info in the wrong section' },
  { id: 'formatting', label: 'Formatting' },
  { id: 'other', label: 'Other' },
];
// Also read by the feedback dashboard to label stored reason ids.
export { REASONS as FEEDBACK_REASONS };

// What started the note generation being rated; stored as comprehend_feedback.source.
// Ratings saved before sources existed are all from the main button.
export const FEEDBACK_SOURCES = {
  comprehend: 'comprehend',
  sectionRefresh: 'section_refresh',
  instructionAssistant: 'instruction_assistant',
};
export const FEEDBACK_SOURCE_LABELS = {
  comprehend: 'Comprehend',
  section_refresh: 'Section refresh',
  instruction_assistant: 'Instruction Assistant',
};
const SECTION_CHOICES = [
  'Subjective', 'Objective', 'Assessment', 'Plan', 'Goals', 'Activity Log',
  'Billing', 'Additional comments', 'Whole note',
];
const MAX_COMMENT_LENGTH = 1000;
const THANKS_MS = 3000;

// Lucide "thumbs-up" / "thumbs-down" (ISC).
const THUMB_PATHS = {
  up: [
    'M7 10v12',
    'M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z',
  ],
  down: [
    'M17 14V2',
    'M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z',
  ],
};

const ThumbIcon = ({ direction }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {THUMB_PATHS[direction].map((d) => (
      <path key={d} d={d} />
    ))}
  </svg>
);

const Chip = ({ label, selected, onClick }) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    style={{
      border: `1px solid ${selected ? SLATE : CHIP_BORDER}`,
      backgroundColor: selected ? SLATE : '#fff',
      color: selected ? '#fff' : TEXT,
      borderRadius: 999,
      padding: '4px 10px',
      fontSize: 13,
      fontWeight: selected ? 600 : 500,
      lineHeight: '18px',
      textAlign: 'left',
      maxWidth: '100%',
      cursor: 'pointer',
    }}
  >
    {label}
  </button>
);

const chipRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginTop: 8,
};

const toggle = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const ComprehendFeedback = ({ onSubmit, onDone, sectionChoices = null }) => {
  const choices = sectionChoices?.length ? sectionChoices : SECTION_CHOICES;
  const askSections = choices.length > 1;
  const [rating, setRating] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [affectedSections, setAffectedSections] = useState([]);
  const [comment, setComment] = useState('');

  const canSend =
    reasons.length > 0 || affectedSections.length > 0 || comment.trim() !== '';

  const finished = rating !== null && !panelOpen;
  useEffect(() => {
    if (!finished) return undefined;
    const timer = setTimeout(onDone, THANKS_MS);
    return () => clearTimeout(timer);
  }, [finished, rating]); // eslint-disable-line react-hooks/exhaustive-deps

  let label = 'How’d we do?';
  if (rating === 'down' && panelOpen) label = 'Noted';
  else if (rating) label = 'Thanks!';

  const rate = (value) => {
    if (rating === value) return;
    setRating(value);
    setPanelOpen(value === 'down');
    onSubmit({ rating: value });
  };

  // "Whole note" and individual sections are mutually exclusive.
  const toggleSection = (section) => {
    if (section === 'Whole note') {
      setAffectedSections((current) =>
        current.includes('Whole note') ? [] : ['Whole note'],
      );
    } else {
      setAffectedSections((current) =>
        toggle(
          current.filter((s) => s !== 'Whole note'),
          section,
        ),
      );
    }
  };

  const send = () => {
    onSubmit({
      rating: 'down',
      reasons,
      sections: affectedSections,
      comment: comment.trim(),
    });
    setPanelOpen(false);
  };

  const thumbButton = (direction) => {
    const selected = rating === direction;
    const selectedColor = direction === 'up' ? GREEN : SLATE;
    return (
      <button
        type="button"
        aria-label={direction === 'up' ? 'Thumbs up' : 'Thumbs down'}
        aria-pressed={selected}
        onClick={() => rate(direction)}
        style={{
          width: 36,
          height: 34,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          borderLeft:
            direction === 'down' ? `1px solid ${GREEN_BORDER}` : 'none',
          backgroundColor: selected ? selectedColor : '#fff',
          color: selected ? '#fff' : TEXT,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <ThumbIcon direction={direction} />
      </button>
    );
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          backgroundColor: '#ecf9f1',
          whiteSpace: 'nowrap',
          borderTop: `1px solid ${GREEN_BORDER}`,
          borderBottom: `1px solid ${GREEN_BORDER}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginRight: 'auto',
            color: GREEN_TEXT,
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          <i
            className="fa fa-check"
            style={{ marginRight: 6 }}
            aria-hidden="true"
          />
          Note complete
        </div>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}
        >
          <span
            aria-live="polite"
            style={{
              color: TEXT,
              fontSize: 13,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {label}
          </span>
          <div
            role="group"
            aria-label="Rate this note"
            style={{
              display: 'inline-flex',
              flexShrink: 0,
              border: `1px solid ${GREEN_BORDER}`,
              borderRadius: 10,
              overflow: 'hidden',
            }}
          >
            {thumbButton('up')}
            {thumbButton('down')}
          </div>
        </div>
      </div>

      {panelOpen && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#f8f9fb',
            borderBottom: '1px solid #e5e7eb',
            color: TEXT,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>
                Got it. What was off?
              </span>{' '}
              <span style={{ fontSize: 12, color: MUTED }}>
                Pick all that apply
              </span>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setPanelOpen(false)}
              style={{
                border: 'none',
                background: 'none',
                padding: 2,
                color: MUTED,
                cursor: 'pointer',
                lineHeight: 0,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>

          <div style={{ marginTop: 6, fontSize: 13, color: MUTED }}>
            When you give feedback we will fix it for you on your next note!
          </div>

          <div style={chipRowStyle}>
            {REASONS.map((reason) => (
              <Chip
                key={reason.id}
                label={reason.label}
                selected={reasons.includes(reason.id)}
                onClick={() => setReasons((current) => toggle(current, reason.id))}
              />
            ))}
          </div>

          {askSections && (
            <>
              <div style={{ marginTop: 12, fontSize: 12, color: MUTED }}>
                <span
                  style={{
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}
                >
                  Which sections were affected?
                </span>{' '}
                Pick all that apply
              </div>
              <div style={chipRowStyle}>
                {choices.map((section) => (
                  <Chip
                    key={section}
                    label={section}
                    selected={affectedSections.includes(section)}
                    onClick={() => toggleSection(section)}
                  />
                ))}
              </div>
            </>
          )}

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 10, marginBottom: 5 }}>
            <label htmlFor="comprehend-feedback-comment" style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>
              Tell us more{' '}
              <span style={{ fontSize: 12, fontWeight: 400, color: MUTED }}>(optional)</span>
            </label>
            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 400, color: MUTED }}>
              {comment.length.toLocaleString()} / {MAX_COMMENT_LENGTH.toLocaleString()}
            </span>
          </div>
          <textarea
            id="comprehend-feedback-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={MAX_COMMENT_LENGTH}
            rows={2}
            placeholder="e.g. I said shoulder flexion was 145°, but the note says 140°"
            style={{
              display: 'block',
              width: '100%',
              padding: '7px 10px',
              border: `1px solid ${CHIP_BORDER}`,
              borderRadius: 8,
              fontSize: 13,
              lineHeight: '19px',
              color: TEXT,
              backgroundColor: '#fff',
              resize: 'vertical',
            }}
          />

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 12,
              marginTop: 10,
            }}
          >
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              style={{
                border: 'none',
                background: 'none',
                padding: '6px 4px',
                color: SLATE,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Skip
            </button>
            <Button
              type="button"
              color="primary"
              disabled={!canSend}
              onClick={send}
              style={{
                margin: 0,
                borderRadius: 999,
                padding: '6px 18px',
                fontSize: 14,
                ...(canSend
                  ? {}
                  : {
                      backgroundColor: '#e9ecef',
                      borderColor: '#e9ecef',
                      color: '#adb5bd',
                      opacity: 1,
                      boxShadow: 'none',
                    }),
              }}
            >
              Send feedback
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComprehendFeedback;
